import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { Decimal } from "decimal.js";

import {
    GET_RATE_HISTORY_ROUTE,
    MARKET_DURATIONS,
    MARKET_QUOTE_FOR_APY_ROUTE,
    MARKET_QUOTE_ROUTE,
    ROUTE_GET_REFINANCE_INFO,
    ROUTE_REFRESH_MARKET_TVL_STATS,
    GET_MIN_PRINCIPAL_ROUTE,
    ROUTE_FETCH_MARKET_INFORMATIONS,
    GET_ALL_MARKET_PRINCIPAL_STATS_ROUTE,
    GET_COLLATERAL_STATS_ROUTE,
    GET_MARKET_BORROW_CAPS_ROUTE,
    GET_MARKET_PRINCIPAL_STATS_ROUTE,
    GET_MARKET_STRATEGY_STATS_ROUTE,
    GET_REFRESH_MARKET_STRATEGY_STATS_ROUTE,
    GET_LOANS_WITH_INFO_ROUTE,
    ROUTE_INCREASE_CREDIT_QUOTE,
    ROUTE_LOAN_SALE_QUOTE,
    GET_BEST_LOOP_QUOTE_ROUTE,
    GET_BEST_QUOTE_ROUTE,
    GET_MAX_QUOTE_ROUTE,
    GET_STRATEGY_INFO_ROUTE
} from "../constants";
import { abfSerializeQueryArgs, marketsPublicBaseQuery } from "./util";
import {
    AbfLoanResponse,
    BestLoopQuote,
    BestLoopQuoteQuery,
    BestQuote,
    BestQuoteFilter,
    BorrowCap,
    CollateralQuoteFilter,
    CollateralQuoteFilterForApy,
    CollateralStats,
    HistoricalMarketRate,
    IncreasePrincipalQuote,
    LendingStrategyFilter,
    LoanFilterApi,
    MarketInformationsFilter,
    MarketPrincipalStats,
    MarketsFilter,
    MarketStratResponse,
    MaxQuote,
    MaxQuoteRequest,
    OrderbookQuote,
    OrderbookQuoteForApy,
    ParsedMarketInformation,
    PrincipalMarketStrategyStats,
    RateMarketType,
    RatesHistoryParams,
    RawMarketStratResponse,
    RawMaxQuote,
    RawParsedMarketInformation,
    RawPrincipalMarketStrategyStats,
    RawStrategyInfo,
    RefinanceInfoParams,
    RefinanceInfoResponse,
    SellLoanQuote,
    StrategyInfo
} from "../types";
import { deserializeStrategyDurationOrUndefined } from "../utils";

const MARKET_INFO_TAG = "MarketInformation";
const MARKETS_PUBLIC_TAG = "MarketsPublic";
const LOANS_TAG = "Loans";
const LENDING_STRATEGY_TAG = "LendingStrategy";

export const marketsPublicApi = createApi({
    reducerPath: "marketsPublicApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    tagTypes: [MARKETS_PUBLIC_TAG, LOANS_TAG, MARKET_INFO_TAG, LENDING_STRATEGY_TAG],
    endpoints: (builder) => ({
        strategyDurations: builder.query<StrategyDuration[], void>({
            // market durations are hardcoded for now to reduce latency
            queryFn() {
                return {
                    data: MARKET_DURATIONS
                };
            }
        }),

        //Loan info route
        loanInfos: builder.query<AbfLoanResponse, LoanFilterApi>({
            providesTags: [LOANS_TAG],
            query(body) {
                return {
                    url: GET_LOANS_WITH_INFO_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),

        // Market stat routes
        marketStats: builder.query<MarketStratResponse, MarketsFilter>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(args) {
                return {
                    url: GET_MARKET_STRATEGY_STATS_ROUTE,
                    method: "POST",
                    body: args
                };
            },
            transformResponse: (raw: RawMarketStratResponse) => convertToMarketStratResponse(raw)
        }),
        refreshMarketStats: builder.query<MarketStratResponse, MarketsFilter>({
            query(args) {
                return {
                    url: GET_REFRESH_MARKET_STRATEGY_STATS_ROUTE,
                    method: "POST",
                    body: args
                };
            },
            transformResponse: (raw: RawMarketStratResponse) => convertToMarketStratResponse(raw)
        }),
        marketPrincipalStatsByMint: builder.query<MarketPrincipalStats, string>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(mint) {
                return {
                    url: `${GET_MARKET_PRINCIPAL_STATS_ROUTE}/${mint}`,
                    method: "GET"
                };
            }
        }),
        collateralStats: builder.query<CollateralStats, void>({
            query() {
                return {
                    url: GET_COLLATERAL_STATS_ROUTE,
                    method: "GET"
                };
            }
        }),
        allMarketPrincipalStats: builder.query<MarketPrincipalStats[], void>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query() {
                return {
                    url: GET_ALL_MARKET_PRINCIPAL_STATS_ROUTE,
                    params: { force: true },
                    method: "GET"
                };
            }
        }),
        borrowCaps: builder.query<BorrowCap[], string[]>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(args) {
                return {
                    url: GET_MARKET_BORROW_CAPS_ROUTE,
                    method: "POST",
                    body: args
                };
            }
        }),
        refreshMarketTvl: builder.query<void, void>({
            query() {
                return {
                    url: ROUTE_REFRESH_MARKET_TVL_STATS,
                    method: "POST"
                };
            }
        }),

        //Market quotes routes
        marketQuotes: builder.query<OrderbookQuote[], CollateralQuoteFilter>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: MARKET_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        marketQuotesForApy: builder.query<OrderbookQuoteForApy[], CollateralQuoteFilterForApy>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: MARKET_QUOTE_FOR_APY_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        refinanceInfo: builder.query<RefinanceInfoResponse, RefinanceInfoParams>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_REFINANCE_INFO,
                    method: "POST",
                    body
                };
            }
        }),
        increasePrincipalQuote: builder.query<IncreasePrincipalQuote, string>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(loanAddress: string) {
                return {
                    url: `${ROUTE_INCREASE_CREDIT_QUOTE}/${loanAddress}`,
                    method: "GET"
                };
            }
        }),
        sellQuote: builder.query<SellLoanQuote, string>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(loanAddress: string) {
                return {
                    url: `${ROUTE_LOAN_SALE_QUOTE}/${loanAddress}`,
                    method: "GET"
                };
            }
        }),

        // Strategy info routes
        lendingStrategyInfos: builder.query<StrategyInfo[], LendingStrategyFilter>({
            providesTags: [LENDING_STRATEGY_TAG],
            query(filter) {
                return {
                    url: GET_STRATEGY_INFO_ROUTE,
                    method: "POST",
                    body: {
                        ...filter
                    }
                };
            },
            transformResponse: (response: RawStrategyInfo[]) => {
                return response.map((strategy) => ({
                    ...strategy,
                    strategy: {
                        ...strategy.strategy,
                        interestPerSecond: new Decimal(strategy.strategy.interestPerSecond)
                    },
                    terms: {
                        assetTerms: {
                            ...Object.fromEntries(
                                Object.entries(strategy.terms.assetTerms).map(([mint, rawAssetTerms]) => [
                                    mint,
                                    {
                                        durationAndApys: rawAssetTerms.durationAndApys,
                                        allocationInfo: {
                                            maxAllocationPct: new Decimal(
                                                rawAssetTerms.allocationInfo.maxAllocationPct
                                            ),
                                            currentAllocationAmount: new Decimal(
                                                rawAssetTerms.allocationInfo.currentAllocationAmount
                                            )
                                        }
                                    }
                                ])
                            )
                        },
                        borrowCaps: {
                            max1hr: new Decimal(strategy.terms.borrowCaps.max1hr),
                            max24hr: new Decimal(strategy.terms.borrowCaps.max24hr),
                            maxOutstanding: new Decimal(strategy.terms.borrowCaps.maxOutstanding)
                        },
                        supplyCaps: {
                            max1hr: new Decimal(strategy.terms.supplyCaps.max1hr),
                            max24hr: new Decimal(strategy.terms.supplyCaps.max24hr),
                            maxOutstanding: new Decimal(strategy.terms.supplyCaps.maxOutstanding)
                        },
                        withdrawCaps: {
                            max1hr: new Decimal(strategy.terms.withdrawCaps.max1hr),
                            max24hr: new Decimal(strategy.terms.withdrawCaps.max24hr),
                            maxOutstanding: new Decimal(strategy.terms.withdrawCaps.maxOutstanding)
                        }
                    },
                    borrowCapMonitor: {
                        startTime1hr: new Decimal(strategy.borrowCapMonitor.startTime1hr),
                        startTime24hr: new Decimal(strategy.borrowCapMonitor.startTime24hr),
                        principal1hr: new Decimal(strategy.borrowCapMonitor.principal1hr),
                        principal24hr: new Decimal(strategy.borrowCapMonitor.principal24hr)
                    },
                    supplyCapMonitor: {
                        startTime1hr: new Decimal(strategy.supplyCapMonitor.startTime1hr),
                        startTime24hr: new Decimal(strategy.supplyCapMonitor.startTime24hr),
                        principal1hr: new Decimal(strategy.supplyCapMonitor.principal1hr),
                        principal24hr: new Decimal(strategy.supplyCapMonitor.principal24hr)
                    },
                    withdrawCapMonitor: {
                        startTime1hr: new Decimal(strategy.withdrawCapMonitor.startTime1hr),
                        startTime24hr: new Decimal(strategy.withdrawCapMonitor.startTime24hr),
                        principal1hr: new Decimal(strategy.withdrawCapMonitor.principal1hr),
                        principal24hr: new Decimal(strategy.withdrawCapMonitor.principal24hr)
                    }
                }));
            }
        }),
        // Returns a map of principal mint to minimum amount.
        presetPrincipalByMints: builder.query<Record<string, number>, string[]>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_MIN_PRINCIPAL_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),

        // markert rates history routes
        ratesHistory: builder.query<Record<RateMarketType, HistoricalMarketRate[]>, RatesHistoryParams>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_RATE_HISTORY_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),

        // Oracle market informations route
        oracleMarketInformations: builder.query<Record<string, ParsedMarketInformation>, MarketInformationsFilter>({
            providesTags: [MARKET_INFO_TAG],
            query(params) {
                return {
                    url: ROUTE_FETCH_MARKET_INFORMATIONS,
                    body: params,
                    method: "POST"
                };
            },
            transformResponse: (response: RawParsedMarketInformation[]) => {
                const record: Record<string, ParsedMarketInformation> = {};
                response.forEach((market) => {
                    record[market.marketInformation.address] = {
                        marketInformation: market.marketInformation,
                        marketInformationAssetInfo: market.marketInformationAssetInfo.map((asset) => ({
                            ...asset,
                            maxCollateralAllocationPct: new Decimal(asset.maxCollateralAllocationPct),
                            currentAllocationAmount: new Decimal(asset.currentAllocationAmount)
                        })),
                        oracleMapping: market.oracleMapping
                    };
                });
                return record;
            }
        }),

        //Quotes
        loopBestQuote: builder.query<BestLoopQuote | null, BestLoopQuoteQuery>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_BEST_LOOP_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        maxQuotes: builder.query<MaxQuote[], MaxQuoteRequest>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_MAX_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: convertRawQuote
        }),
        bestQuotes: builder.query<BestQuote[], BestQuoteFilter>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_BEST_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: convertRawBestQuotes
        })
    })
});

export const {
    useStrategyDurationsQuery,
    useMarketStatsQuery,
    useMarketQuotesQuery,
    useMarketQuotesForApyQuery,
    useAllMarketPrincipalStatsQuery,
    useMarketPrincipalStatsByMintQuery,
    useRefinanceInfoQuery,
    useBorrowCapsQuery,
    usePresetPrincipalByMintsQuery,
    useRatesHistoryQuery,
    useRefreshMarketStatsQuery,
    useRefreshMarketTvlQuery,
    useOracleMarketInformationsQuery,
    useLoanInfosQuery,
    useCollateralStatsQuery,
    useIncreasePrincipalQuoteQuery,
    useSellQuoteQuery,
    useLendingStrategyInfosQuery,
    useLoopBestQuoteQuery,
    useMaxQuotesQuery,
    useBestQuotesQuery
} = marketsPublicApi;

export const useMarketsPublicApi = () => {
    const api = marketsPublicApi.endpoints;

    const dispatch = useDispatch();
    const [fetchMarketStats] = marketsPublicApi.endpoints.marketStats.useLazyQuery();
    const [fetchLoanInfos] = api.loanInfos.useLazyQuery();

    return {
        fetchMarketStats,
        resetMarketsPublicApi: () => dispatch(marketsPublicApi.util.invalidateTags([MARKETS_PUBLIC_TAG])),
        resetMarketInfoApi: () => dispatch(marketsPublicApi.util.invalidateTags([MARKET_INFO_TAG])),
        resetLendingStrategyApi: () => dispatch(marketsPublicApi.util.invalidateTags([LENDING_STRATEGY_TAG])),
        resetLoanApi: () => dispatch(marketsPublicApi.util.invalidateTags([LOANS_TAG])),
        fetchLoanInfos
    };
};

function convertToMarketStratResponse(input: RawMarketStratResponse): MarketStratResponse {
    const result: MarketStratResponse = {};

    for (const [mint, data] of Object.entries(input)) {
        result[mint] = transformPrincipalMarketStats(data);
    }

    return result;
}

export function transformPrincipalMarketStats(raw: RawPrincipalMarketStrategyStats): PrincipalMarketStrategyStats {
    return {
        totalDeposits: raw.totalDeposits,
        durationToMinApy: Object.entries(raw.durationToMinApy).map(([key, apy]) => {
            const strategyDuration = deserializeStrategyDurationOrUndefined(key);
            if (!strategyDuration) {
                throw new Error(`Invalid strategy duration: ${key}`);
            }
            return {
                duration: strategyDuration.duration,
                durationType: strategyDuration.durationType,
                apy: apy
            };
        })
    };
}

function convertRawQuote(raw: RawMaxQuote[]) {
    return raw.map((quote) => {
        return {
            apy: quote.apy,
            lendingStrategyAddress: quote.strategy,
            ltv: quote.ltv,
            liquidationThreshold: quote.lqt,
            principalAvailable: quote.amount,
            collateralMint: quote.collateralIdentifier
        };
    });
}

function convertRawBestQuotes(raw: Record<string, Omit<BestQuote, "collateralMint">[]>) {
    return Object.entries(raw)
        .map(([collateralMint, quotes]) => quotes.map((bestQuote): BestQuote => ({ ...bestQuote, collateralMint })))
        .flat();
}
